/**
 * Приём платежа + список банковских транзакций.
 * Разметка 1:1 с legacy htdocs/core/templates/admin_transactions.tpl
 * (классы admin.css: .addtransaction/.clhead/.clbody, .banktransaction/.athead/.atbody,
 * .transactiontable, .searcharea) — HTML, который legacy собирал
 * в JS-шаблонах initTransactionList(), воспроизведён тегами React как есть.
 *
 * Исключение — окно зачёта/переназначения: самодельный модал legacy
 * (.settransactionmodal/.settransactionmodalbk) заменён общим <Modal> (закрытие по фону
 * и Esc, блокировка прокрутки, возврат фокуса). Поля, тексты и запросы те же.
 *
 * Поведение legacy-скрипта сохранено:
 *  - «Зачесть»  → POST /api/admin/transactions/link    (legacy /admin/linkTransactionToArea)
 *  - «✏️»       → POST /api/admin/transactions/recount (legacy /admin/recountTransactionToArea)
 *  - «Добавить платеж» → POST /api/admin/transactions/add (legacy /admin/addTransactionToArea)
 *  - те же проверки формы и тексты ошибок, чекбокс «Информирование» включён по умолчанию,
 *    «(недостаточно средств)» гаснет через 10 секунд.
 *
 * Отличия (см. notes): datetimepicker → input[type=text] с теми же классами fildate,
 * bootstrap-select → обычный <select> с теми же классами, tablesorter не подключается.
 */

import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { api, ApiError } from '@/api/client'
import { Modal } from '@/components/Modal'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

/** Платёж из реестра эквайринга (legacy GetTransactionList → paymentslist). */
interface RegistryPayment {
  id: number
  date_end: string | null
  summ: string
  areaname: string | null
}

/** Банковская операция (t_bankopen_history + подобранный участок). */
interface BankOperationRow {
  id: number
  documentid: string | null
  user_transaction_id: number | null
  paymentpurpose: string | null
  payerinfo_name: string | null
  documentamount_amount: string
  settlementdate: string | null
  paymentregisterdate: string | null
  areaname: string | null
  areaid: number | null
  paymentslist?: RegistryPayment[]
}

/** Ответ GET /api/admin/transactions (Handlers\Admin\Transactions::list). */
interface TransactionsResponse {
  bankopen_status: boolean
  bankopen_message: string
  startdate: string
  enddate: string
  transactions: BankOperationRow[]
}

/** Строка GET /api/admin/areas — для селекта «Выберите участок» (legacy /admin/area_list). */
interface AreaOption {
  id: number
  name: string
  familia: string | null
  imuy: string | null
  otchestvo: string | null
}

/** Ответ link/recount: имя участка (проверки баланса SMS-шлюза больше нет — канал отключён). */
interface LinkResponse {
  areaname?: string
  status?: string
}

/** legacy date_normalize(): «2024-03-15 12:00:00» → «15.03.2024 » (с хвостовым пробелом). */
function dateNormalize(value: string | null): string {
  if (value === null || value === '') return ''
  const date = new Date(value.replace(' ', 'T'))
  const day = date.getDate()
  const month = date.getMonth() + 1
  return `${day < 10 ? '0' + day : day}.${month < 10 ? '0' + month : month}.${date.getFullYear()} `
}

/** legacy date_get_time(): скрытый ключ сортировки строки таблицы. */
function dateGetTime(value: string | null): string {
  if (value === null || value === '') return ''
  return String(new Date(value.replace(' ', 'T')).getTime())
}

/** legacy: скрытый ключ сортировки суммы — parseFloat(«1 234,56».replace(' ','')) * 100. */
function amountSortKey(amount: string): string {
  return String(Number.parseFloat(amount.replace(' ', '')) * 100)
}

/** Дата d.m.Y — формат полей фильтра (legacy datetimepicker format:'d.m.Y'). */
function formatFilterDate(date: Date): string {
  const day = date.getDate()
  const month = date.getMonth() + 1
  return `${day < 10 ? '0' + day : day}.${month < 10 ? '0' + month : month}.${date.getFullYear()}`
}

/** Опция селекта участков (legacy: «Иванов Иван Иванович (участок 12)»). */
function areaOptionLabel(area: AreaOption): string {
  const familia = area.familia ?? ''
  const imuy = area.imuy ?? ''
  const otchestvo = area.otchestvo ?? ''
  return `${familia} ${imuy} ${otchestvo} (участок ${area.name})`
}

/** Открытая модалка зачёта/переназначения (legacy .settransactionmodal). */
interface ModalState {
  kind: 'link' | 'recount'
  transactionId: number
  fromArea: number | null
}

export function AdminTransactionsPage() {
  // те же стили, что подключал legacy admin.tpl + admin_transactions.tpl
  useLegacyCss('admin', 'newapproach', 'bootstrap-select.min', 'jquery.datetimepicker', 'theme.default.min')

  // legacy: период по умолчанию — последние 30 дней (Transactions::list)
  const defaults = useMemo(
    () => ({
      startdate: formatFilterDate(new Date(Date.now() - 30 * 24 * 3600 * 1000)),
      enddate: formatFilterDate(new Date()),
    }),
    [],
  )

  const [startDate, setStartDate] = useState(defaults.startdate)
  const [endDate, setEndDate] = useState(defaults.enddate)
  const [applied, setApplied] = useState(defaults)

  const { data, reload } = useApi<TransactionsResponse>(
    `/api/admin/transactions?startdate=${encodeURIComponent(applied.startdate)}` +
      `&enddate=${encodeURIComponent(applied.enddate)}`,
  )
  const { data: areasData } = useApi<AreaOption[]>('/api/admin/areas')

  // legacy: блок «Прием платежа» на загрузке сворачивается (clcollapse по всем .clcollapse)
  const [collapsed, setCollapsed] = useState(true)

  const [areaId, setAreaId] = useState('0')
  const [money, setMoney] = useState('')
  const [comment, setComment] = useState('')
  const [formError, setFormError] = useState('')

  const [modal, setModal] = useState<ModalState | null>(null)
  const [modalArea, setModalArea] = useState('0')
  const [inform, setInform] = useState(true)
  /** Зачтённые прямо сейчас строки: legacy подменял содержимое ячейки без перезагрузки. */
  const [linked, setLinked] = useState<Record<number, string>>({})

  const transactions = data?.transactions ?? []
  // legacy area_list: участки без ФИО в селект не попадают
  const areas = (areasData ?? []).filter(
    (area) => area.familia !== null || area.imuy !== null || area.otchestvo !== null,
  )

  const openModal = (state: ModalState): void => {
    setModal(state)
    setModalArea('0')
    setInform(true)
  }

  const closeModal = (): void => setModal(null)

  /** «Зачесть» / «Переназначить» в модалке. */
  const submitModal = async (): Promise<void> => {
    if (modal === null) return

    if (modal.kind === 'link') {
      const result = await api.post<LinkResponse>('/api/admin/transactions/link', {
        transactionid: modal.transactionId,
        areaid: modalArea,
        informtranstoarea: inform ? 'true' : 'false',
      })
      setLinked((current) => ({ ...current, [modal.transactionId]: result.areaname ?? '' }))
      closeModal()
      return
    }

    const result = await api.post<LinkResponse>('/api/admin/transactions/recount', {
      transactionid: modal.transactionId,
      fromArea: modal.fromArea,
      areaid: modalArea,
      informtranstoarea: inform ? 'true' : 'false',
    })
    if (result.status === 'success') {
      closeModal()
      reload() // legacy: location.reload()
    }
  }

  /** Ручной приём платежа (legacy .transactionadd). */
  const addTransaction = async (): Promise<void> => {
    if (areaId === '0') {
      setFormError('Выберите участок')
      return
    }
    if (money.length === 0) {
      setFormError('Введите сумму')
      return
    }
    if (Number.parseFloat(money.replace(',', '.')) <= 0.0) {
      setFormError('Сумма не может быть меньше 0 рублей')
      return
    }
    if (comment.length === 0) {
      setFormError('Введите назначение платежа')
      return
    }

    try {
      await api.post('/api/admin/transactions/add', {
        areaid: areaId,
        moneycount: money,
        comment,
      })
      reload()
      setAreaId('0')
      setMoney('')
      setComment('')
      setFormError('')
    } catch (e: unknown) {
      setFormError(e instanceof ApiError ? e.message : 'Ошибка при добавлении платежа')
    }
  }

  /** legacy: поле суммы принимает только [0-9,.], максимум 9 символов. */
  const onMoneyInput = (value: string): void => {
    if (/^[0-9,.]{0,9}$/.test(value)) setMoney(value)
  }

  return (
    <>
      {/* legacy .settransactionmodal (500px, по центру) — теперь общий Modal */}
      <Modal
        open={modal !== null}
        onClose={closeModal}
        title={
          modal?.kind === 'recount'
            ? 'Переназначить транзакцию на другой участок'
            : 'Зачесть транзакцию для участка'
        }
        dialogStyle={{ width: 500, maxWidth: '100%' }}
        footer={
          <>
            <div className="inform pull-left" style={{ textAlign: 'left' }}>
              <label style={{ fontWeight: 400, marginBottom: 0 }}>
                <input
                  type="checkbox"
                  className="informtranstoarea"
                  checked={inform}
                  onChange={(e) => setInform(e.target.checked)}
                />
                Информирование
              </label>
            </div>
            <button
              type="button"
              className="btn btn-default settranstoarea"
              onClick={() => void submitModal()}
            >
              {modal?.kind === 'recount' ? 'Переназначить' : 'Зачесть'}
            </button>
          </>
        }
      >
        <select
          className="selectarea"
          data-live-search="true"
          data-size="10"
          data-width="100%"
          value={modalArea}
          onChange={(e) => setModalArea(e.target.value)}
          style={{ width: '100%', height: 35, color: modalArea === '0' ? '#8e8e8e' : 'black' }}
        >
          <option value="0">Выберите участок</option>
          {areas.map((area) => (
            <option key={area.id} value={area.id}>
              {areaOptionLabel(area)}
            </option>
          ))}
        </select>
      </Modal>

      <div className="row">
        <div className="col-md-12 nompnmob">
          <div className="addtransaction">
            <div className="clhead" style={{ borderBottom: collapsed ? '1px solid #ccc' : '0px' }}>
              <div className="clhtitle clcollapse" onClick={() => setCollapsed(!collapsed)}>
                Прием платежа{' '}
                <span className="collapsespan">
                  <i className={collapsed ? 'fas fa-angle-double-down' : 'fas fa-angle-double-up'} />
                </span>
              </div>
            </div>
            <div className="clbody" style={{ display: collapsed ? 'none' : 'block' }}>
              <div className="errorinformblock">
                {formError !== '' && <div className="errorinform">{formError}</div>}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                <div className="searchareablock">
                  <div className="searcharea">
                    <select
                      className="searchareasel"
                      data-live-search="true"
                      data-container="body"
                      data-size="10"
                      data-width="auto"
                      value={areaId}
                      onChange={(e) => setAreaId(e.target.value)}
                      style={{ color: areaId === '0' ? '#8e8e8e' : 'black' }}
                    >
                      <option value="0">Выберите участок</option>
                      {areas.map((area) => (
                        <option key={area.id} value={area.id}>
                          {areaOptionLabel(area)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="sercharearesult" />

                <div className="transactionmoneyblock">
                  <div className="transactionmoney">
                    <input
                      type="text"
                      className="transactionmoneyinput"
                      placeholder="Сумма"
                      value={money}
                      onChange={(e) => onMoneyInput(e.target.value)}
                    />
                  </div>
                </div>

                <div className="transactioncommentblock">
                  <div className="transactioncomment">
                    <input
                      type="text"
                      className="transactioncommentinput"
                      placeholder="Назначение платежа"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                  </div>
                </div>

                <div className="transactionadd" onClick={() => void addTransaction()}>
                  Добавить
                  <div className="hidemob" style={{ display: 'initial' }}>
                    {' '}
                    платеж
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-12 nompnmob">
          <div className="banktransaction">
            <div className="athead">
              Список транзакций
              <div className="bankapistatus">
                Соединение с банком:{' '}
                {data?.bankopen_status === true ? (
                  <span style={{ color: 'green', fontWeight: 'bold' }}>OK</span>
                ) : (
                  <span style={{ color: 'red', fontWeight: 'bold' }}>Ошибка</span>
                )}
              </div>
            </div>
            <div className="atbody">
              <div className="transactions_setdate">
                <div className="datefrom">
                  С
                  <input
                    type="text"
                    name="startfildate"
                    className="startfildate fildate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="dateto">
                  До
                  <input
                    type="text"
                    name="endfildate"
                    className="endfildate fildate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                <input type="hidden" name="group" value="0" />
                <button
                  className="showtransactions"
                  onClick={() => setApplied({ startdate: startDate, enddate: endDate })}
                >
                  Показать
                </button>
              </div>

              <div className="divtransactiontable">
                {/* Шрифт как в legacy: там таблицу инициализировал tablesorter и класс
                    .tablesorter-default задавал font:12px/18px. В rework сортировки нет,
                    поэтому класс не вешаем, а размер выставляем явно — иначе шрифт крупнее. */}
                <table className="transactiontable" style={{ font: '12px/18px Arial, Sans-serif' }}>
                  <thead>
                    <tr>
                      <th className="nosort number">№</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Р/д</th>
                      <th>Сумма</th>
                      <th>От</th>
                      <th>Назначение платежа</th>
                      <th>Зачесть</th>
                    </tr>
                  </thead>
                  <tbody className="tbodylist">
                    {transactions.map((row) => {
                      const registry = row.paymentslist
                      const hasRegistry = registry !== undefined
                      const registryEmpty = registry !== undefined && registry.length === 0
                      // legacy: ручной платёж (без documentid) помечается классом paymenthandadd
                      const handadd = row.documentid === null ? 'paymenthandadd' : ''
                      // legacy: после зачёта имя участка подставляется в ячейку без перезагрузки
                      const justLinked: string | undefined = linked[row.id]
                      const areaName: string | null = justLinked ?? row.areaname

                      /** Ячейка «Зачесть» — порядок условий legacy сохранён. */
                      const linkCell = (): ReactNode => {
                        if (areaName !== null) {
                          return (
                            <>
                              Участок {areaName}
                              {row.areaid !== null && (
                                <div
                                  className="recount"
                                  onClick={() =>
                                    openModal({
                                      kind: 'recount',
                                      transactionId: row.id,
                                      fromArea: row.areaid,
                                    })
                                  }
                                >
                                  ✏️
                                </div>
                              )}
                            </>
                          )
                        }
                        if (registryEmpty) return ''
                        if (row.user_transaction_id !== null || hasRegistry) return 'Зачтено'
                        return (
                          <div
                            className="settransactiontoarea"
                            onClick={() =>
                              openModal({ kind: 'link', transactionId: row.id, fromArea: null })
                            }
                          >
                            Зачесть
                          </div>
                        )
                      }

                      return (
                        <tr
                          key={row.id}
                          className={hasRegistry ? 'reestr' : undefined}
                          data-reestrid={hasRegistry ? row.documentid ?? undefined : undefined}
                        >
                          <td className={`jnumber rownums trvm1 ${handadd}`} />
                          <td className={`trvm2 ${handadd}`}>
                            <span className="tditemhidden">{dateGetTime(row.settlementdate)}</span>{' '}
                            {dateNormalize(row.settlementdate)}
                          </td>
                          <td className={`trvm3 ${handadd}`}>
                            <span className="tditemhidden">
                              {amountSortKey(row.documentamount_amount)}
                            </span>{' '}
                            {row.documentamount_amount + ' руб.'}
                          </td>
                          <td className={`trvm4 ${handadd}`}>{row.payerinfo_name ?? ''}</td>
                          <td className={`trvm4 ${handadd}`}>
                            {row.paymentpurpose}
                            {registry !== undefined && (
                              <table
                                className={`reestr${row.documentid ?? ''}`}
                                style={{ width: '100%' }}
                              >
                                <thead>
                                  <tr>
                                    <th>№</th>
                                    <th>Дата</th>
                                    <th>Сумма</th>
                                    <th>Участок</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {registryEmpty ? (
                                    <tr>
                                      <td colSpan={4} style={{ textAlign: 'center' }}>
                                        Нет данных
                                      </td>
                                    </tr>
                                  ) : (
                                    registry.map((payment) => (
                                      <tr key={payment.id}>
                                        <td className="jnumber rownums trvm1" />
                                        <td className="trvm2">{dateNormalize(payment.date_end)}</td>
                                        <td>{payment.summ + ' руб.'}</td>
                                        <td>{payment.areaname ?? ''}</td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            )}
                          </td>
                          <td className={`trvm5 ${handadd}`}>{linkCell()}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
